// =============================================================================
// SEVRIC Connect — data layer (Firestore + Storage)
// -----------------------------------------------------------------------------
// Collections (default Firestore db, same conventions as vehicles/vendors —
// top-level, tenantId on every row, rules do the gating):
//
//   connectRooms/{roomId}
//     tenantId, kind: 'team' | 'dm' | 'unit' | 'ai', name, memberUids[],
//     vehicleId?, createdAt, createdBy, lastMessage {text, name, at}, msgCount
//   connectRooms/{roomId}/messages/{mid}
//     tenantId, roomId, uid, name, initials, text, at, kind, photos[], voice{},
//     reactions {emoji: [uid]}, replyCount, mentions[], sys?
//   connectRooms/{roomId}/messages/{mid}/replies/{rid}   (thread replies)
//   connectPresence/{uid}       tenantId, name, initials, role, state, at
//   connectReads/{uid}          tenantId, rooms: { roomId: seenCount }
//
// Storage:
//   connect/{tenantId}/{roomId}/{filename}   images (compressed) + voice (webm/opus)
//
// Photos never live in Firestore — only a path + download URL + dimensions.
// Every upload is compressed client-side first (see compressImage) so a 5 MB
// phone photo lands as ~250 KB. Storage rules cap files at 5 MB and pin
// content types, so nothing else can be dropped into the bucket.
// =============================================================================
import {
  collection, doc, addDoc, setDoc, getDoc, getDocs, updateDoc, onSnapshot, query, where,
  orderBy, limit, serverTimestamp, arrayUnion, arrayRemove, increment,
} from 'firebase/firestore';
import { getStorage, ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { httpsCallable } from 'firebase/functions';
import { db, app, functions } from '../lib/firebase.js';

export const AI_UID = 'sevric-ai';
const storage = getStorage(app);

export const initialsOf = (name = '') => {
  const p = String(name).trim().split(/[\s.]+/).filter(Boolean);
  if (!p.length) return '?';
  return (p[0][0] + (p[1]?.[0] || '')).toUpperCase();
};

// ---------- room ids (deterministic where it helps) ----------
export const dmRoomId   = (a, b) => `dm_${[a, b].sort().join('_')}`;
export const unitRoomId = (vehicleId) => `unit_${vehicleId}`;
export const aiRoomId   = (uid) => `ai_${uid}`;

// ---------- presence / directory ----------
export async function touchPresence(profile, state = 'on') {
  if (!profile?.uid || !profile?.tenantId) return;
  await setDoc(doc(db, 'connectPresence', profile.uid), {
    tenantId: profile.tenantId, uid: profile.uid, name: profile.name || profile.email,
    initials: initialsOf(profile.name || profile.email), role: profile.role || 'user',
    state, at: serverTimestamp(),
  }, { merge: true });
}
export function watchPresence(tenantId, cb) {
  const q = query(collection(db, 'connectPresence'), where('tenantId', '==', tenantId));
  return onSnapshot(q, (snap) => cb(snap.docs.map((d) => ({ id: d.id, ...d.data() }))));
}

// ---------- rooms ----------
export function watchRooms(tenantId, uid, cb) {
  // Team + unit rooms are tenant-wide. DMs/AI rooms carry memberUids; rules
  // hide the ones you're not in, so we can query broadly and filter client-side.
  const q = query(collection(db, 'connectRooms'), where('tenantId', '==', tenantId), where('memberUids', 'array-contains', uid));
  const q2 = query(collection(db, 'connectRooms'), where('tenantId', '==', tenantId), where('kind', 'in', ['team', 'unit']));
  const state = { a: [], b: [] };
  const emit = () => { const m = new Map(); [...state.a, ...state.b].forEach((r) => m.set(r.id, r)); cb([...m.values()]); };
  const u1 = onSnapshot(q,  (s) => { state.a = s.docs.map((d) => ({ id: d.id, ...d.data() })); emit(); });
  const u2 = onSnapshot(q2, (s) => { state.b = s.docs.map((d) => ({ id: d.id, ...d.data() })); emit(); });
  return () => { u1(); u2(); };
}

export async function ensureRoom(id, data) {
  const r = doc(db, 'connectRooms', id);
  const snap = await getDoc(r);
  if (!snap.exists()) await setDoc(r, { ...data, createdAt: serverTimestamp(), msgCount: 0, lastMessage: null });
  return id;
}
export async function createTeamRoom(profile, name, storeId = null) {
  const r = await addDoc(collection(db, 'connectRooms'), {
    tenantId: profile.tenantId, storeId, kind: 'team', name, memberUids: [], createdBy: profile.uid,
    createdAt: serverTimestamp(), msgCount: 0, lastMessage: null,
  });
  return r.id;
}
export async function seedDefaultRooms(profile) {
  const q = query(collection(db, 'connectRooms'), where('tenantId', '==', profile.tenantId), where('kind', '==', 'team'));
  const s = await getDocs(q);
  if (!s.empty) return;
  for (const name of ['The Store', 'The Shop', 'Detail', 'Sales Desk', 'Porters']) await createTeamRoom(profile, name);
}
export async function openDM(profile, other) {
  const id = dmRoomId(profile.uid, other.uid);
  await ensureRoom(id, { tenantId: profile.tenantId, kind: 'dm', name: other.name, memberUids: [profile.uid, other.uid], memberNames: { [profile.uid]: profile.name, [other.uid]: other.name }, createdBy: profile.uid });
  return id;
}
export async function openUnitRoom(profile, vehicle) {
  const id = unitRoomId(vehicle.id);
  const name = [vehicle.year, vehicle.make, vehicle.model].filter(Boolean).join(' ') || `Unit ${vehicle.stockNumber || vehicle.id}`;
  await ensureRoom(id, { tenantId: profile.tenantId, kind: 'unit', name, vehicleId: vehicle.id, stockNumber: vehicle.stockNumber || null, memberUids: [], createdBy: profile.uid });
  return id;
}
export async function openAIRoom(profile) {
  const id = aiRoomId(profile.uid);
  await ensureRoom(id, { tenantId: profile.tenantId, kind: 'ai', name: 'SEVRIC', memberUids: [profile.uid], createdBy: profile.uid });
  return id;
}

// ---------- messages ----------
export function watchMessages(roomId, cb, n = 200) {
  const q = query(collection(db, 'connectRooms', roomId, 'messages'), orderBy('at', 'asc'), limit(n));
  return onSnapshot(q, (s) => cb(s.docs.map((d) => ({ id: d.id, ...d.data() }))));
}
export function watchReplies(roomId, mid, cb) {
  const q = query(collection(db, 'connectRooms', roomId, 'messages', mid, 'replies'), orderBy('at', 'asc'));
  return onSnapshot(q, (s) => cb(s.docs.map((d) => ({ id: d.id, ...d.data() }))));
}
function base(profile, roomId) {
  return { tenantId: profile.tenantId, roomId, uid: profile.uid, name: profile.name || profile.email, initials: initialsOf(profile.name || profile.email), at: serverTimestamp(), reactions: {}, replyCount: 0 };
}
export const parseMentions = (text, people) => {
  const out = [];
  const re = /@([A-Za-z]+)/g; let m;
  while ((m = re.exec(text))) {
    const first = m[1].toLowerCase();
    if (first === 'sevric') { out.push(AI_UID); continue; }
    const hit = people.find((p) => (p.name || '').toLowerCase().split(' ')[0] === first);
    if (hit) out.push(hit.uid);
  }
  return [...new Set(out)];
};
export async function sendText(profile, roomId, text, people = []) {
  const mentions = parseMentions(text, people);
  return addDoc(collection(db, 'connectRooms', roomId, 'messages'), { ...base(profile, roomId), kind: 'text', text, mentions });
}
export async function sendReply(profile, roomId, mid, text) {
  await addDoc(collection(db, 'connectRooms', roomId, 'messages', mid, 'replies'), { ...base(profile, roomId), kind: 'text', text, parentId: mid });
  await updateDoc(doc(db, 'connectRooms', roomId, 'messages', mid), { replyCount: increment(1), lastReplyAt: serverTimestamp() });
}
export async function toggleReaction(roomId, mid, emoji, uid, has) {
  await updateDoc(doc(db, 'connectRooms', roomId, 'messages', mid), { [`reactions.${emoji}`]: has ? arrayRemove(uid) : arrayUnion(uid) });
}

// ---------- uploads ----------
export async function compressImage(file, maxEdge = 1600, quality = 0.82) {
  const bmp = await createImageBitmap(file).catch(() => null);
  if (!bmp) return { blob: file, w: 0, h: 0 };
  const scale = Math.min(1, maxEdge / Math.max(bmp.width, bmp.height));
  const w = Math.round(bmp.width * scale), h = Math.round(bmp.height * scale);
  const c = document.createElement('canvas'); c.width = w; c.height = h;
  c.getContext('2d').drawImage(bmp, 0, 0, w, h);
  const blob = await new Promise((res) => c.toBlob(res, 'image/jpeg', quality));
  return { blob: blob || file, w, h };
}
export async function sendPhotos(profile, roomId, files, caption = '') {
  const photos = [];
  for (const f of Array.from(files).slice(0, 6)) {
    const { blob, w, h } = await compressImage(f);
    const name = `${Date.now()}_${Math.random().toString(36).slice(2, 8)}.jpg`;
    const path = `connect/${profile.tenantId}/${roomId}/${name}`;
    const r = ref(storage, path);
    await uploadBytes(r, blob, { contentType: 'image/jpeg', cacheControl: 'public,max-age=31536000' });
    photos.push({ path, url: await getDownloadURL(r), w, h, bytes: blob.size });
  }
  return addDoc(collection(db, 'connectRooms', roomId, 'messages'), { ...base(profile, roomId), kind: 'photo', text: caption, photos, mentions: [] });
}
export async function sendVoice(profile, roomId, blob, durSec) {
  const name = `${Date.now()}_${Math.random().toString(36).slice(2, 8)}.webm`;
  const path = `connect/${profile.tenantId}/${roomId}/${name}`;
  const r = ref(storage, path);
  await uploadBytes(r, blob, { contentType: blob.type || 'audio/webm' });
  return addDoc(collection(db, 'connectRooms', roomId, 'messages'), { ...base(profile, roomId), kind: 'voice', text: '', voice: { path, url: await getDownloadURL(r), dur: durSec, bytes: blob.size }, mentions: [] });
}

// ---------- reads / unread ----------
export function watchReads(uid, cb) { return onSnapshot(doc(db, 'connectReads', uid), (s) => cb(s.exists() ? s.data().rooms || {} : {})); }
export async function markRead(profile, roomId, count) {
  await setDoc(doc(db, 'connectReads', profile.uid), { tenantId: profile.tenantId, uid: profile.uid, rooms: { [roomId]: count } }, { merge: true });
}

// ---------- SEVRIC AI ----------
export async function catchMeUp(roomId) {
  const fn = httpsCallable(functions, 'connect_catchup');
  const r = await fn({ roomId });
  return r.data?.text || '';
}
export async function askSevric(roomId, text) {
  const fn = httpsCallable(functions, 'connect_ai');
  const r = await fn({ roomId, text });
  return r.data?.text || '';
}
